/**
 * TimelinePhaseLayer Component
 *
 * Renders phase rectangles for all crops.
 * Handles both collapsed and expanded views.
 */

import React, { useMemo, useCallback } from 'react'
import { Typography, darken } from '@mui/material'
import {
  useTimelineContextDispatch,
  setExpandedIds as setExpandedIdsAction,
  setHoveredPhaseKey as setHoveredPhaseKeyAction,
} from '../../context/timeline-context'
import { useTimelineStaticContext } from '../../context/timeline-static-context'
import { useTimelineInteractionContext } from '../../context/timeline-interaction-context'
import { useOrgHoverContext } from '~/common/context/organization/org-context'
import { useTimelineStyles } from '../../../hooks/use-timeline-styles'
import {
  EXPANDED_ROW_HEIGHT,
  EXPANDED_ROW_HEIGHT_GAP,
  VERTICAL_DAY_TRACKER_SPACE,
} from '../../timeline-constants'
import { PannableRect } from './pannable-rect'

// Stable sx object — defined once at module level, never reallocated
const ICON_SX = {
  fontSize: 14,
  lineHeight: 1,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
}

/**
 * TimelinePhaseLayer - Phase rectangles for all crops
 *
 * Renders phase bars with icons. Click to expand/collapse in non-interactive mode.
 *
 * @param {Object} props
 * @param {Array} props.crops - Transformed crop data with week-based phases
 * @param {boolean} props.interactive - Enable interactive mode (disables click to expand)
 */
export const TimelinePhaseLayer = React.memo(({ crops, interactive, onPanStart, isSchedulePanning }) => {
  const {
    expandedIds,
    weekWidth,
    getRowY,
    forceAllExpand,
    viewStrategy,
    collapsedRowHeight,
    collapsedRowHeightGap,
  } = useTimelineStaticContext()
  const { hoveredPhaseKey } = useTimelineInteractionContext()

  const dispatch = useTimelineContextDispatch()
  const { hoveredOrg } = useOrgHoverContext()
  const { getPhaseStyle, PHASE_BORDER_COLOR } = useTimelineStyles()

  // Stable delegated hover handlers — one pair per component, not per rect
  const handleMouseOver = useCallback((e) => {
    const phaseKey = e.target.dataset.phaseKey ?? null
    dispatch(setHoveredPhaseKeyAction({ hoveredPhaseKey: phaseKey }))
  }, [dispatch])
  const handleMouseLeave = useCallback(
    () => dispatch(setHoveredPhaseKeyAction({ hoveredPhaseKey: null })),
    [dispatch]
  )

  // Sorted phase indices per crop — only re-computes when crops data or hovered phase changes.
  // Avoids O(N log N) sort on every render; sort result is stable until hover changes.
  const sortedPhaseIndices = useMemo(() => {
    const map = new Map()
    crops.forEach(crop => {
      if (crop.isOrgTaskRow) return
      const indices = crop.phases.map((_, i) => i)
      indices.sort((a, b) => {
        if (`${crop.id}-${a}` === hoveredPhaseKey) return 1
        if (`${crop.id}-${b}` === hoveredPhaseKey) return -1
        return 0
      })
      map.set(crop.id, indices)
    })
    return map
  }, [crops, hoveredPhaseKey])

  return (
    <>
      {crops.map((crop) => {
        if (crop.isOrgTaskRow) return null  // Org task rows have no phases

        const isExpanded = forceAllExpand || expandedIds.includes(crop.id)
        const rowY = getRowY(crop.id) + VERTICAL_DAY_TRACKER_SPACE * 8

        // Calculate row height: double when showing both phases and tasks
        const baseRowH = isExpanded ? EXPANDED_ROW_HEIGHT : collapsedRowHeight
        const rowH = viewStrategy === 'both' && !crop.isOrgTaskRow ? baseRowH * 2 : baseRowH

        const barH = isExpanded
          ? EXPANDED_ROW_HEIGHT - EXPANDED_ROW_HEIGHT_GAP
          : collapsedRowHeight - collapsedRowHeightGap

        // Calculate sub-row height when both phases and tasks shown
        const subRowH = viewStrategy === 'both' && !crop.isOrgTaskRow ? rowH / 2 : rowH

        // Position in top half when both, otherwise center in full row
        const y = viewStrategy === 'both' && !crop.isOrgTaskRow
          ? rowY + (subRowH / 2) - (barH / 2)
          : rowY + (rowH / 2) - (barH / 2)

        return (
          // Delegated hover: onMouseOver reads data-phase-key from the target rect,
          // onMouseLeave fires once when the mouse exits the entire crop row.
          <g key={crop.id} onMouseOver={handleMouseOver} onMouseLeave={handleMouseLeave}>
            {(sortedPhaseIndices.get(crop.id) ?? []).map((pidx) => {
              const phase = crop.phases[pidx]
              const s = getPhaseStyle(phase.type)
              const x = phase.start * weekWidth
              const phaseWidth = phase.len * weekWidth
              const isParentOrgHovered = crop.organization_id === hoveredOrg?.id
              const phaseKey = `${crop.id}-${pidx}`

              return (
                <g key={pidx}>
                  {(interactive || crop.interactive) ? (
                    /* Interactive mode: panning enabled, expand/collapse disabled */
                    <PannableRect
                      data-phase-key={phaseKey}
                      x={x}
                      y={y}
                      width={phaseWidth}
                      height={barH}
                      fill={isParentOrgHovered ? darken(s.color, 0.2) : s.color}
                      rx={6}
                      stroke={PHASE_BORDER_COLOR}
                      strokeWidth={isParentOrgHovered ? 1 : 0.25}
                      isPanning={isSchedulePanning}
                      onPanStart={onPanStart}
                    >
                      {/* Phase icon */}
                      <foreignObject
                        x={x + 5}
                        y={isExpanded ? y + 6 : y + (barH / 2) - (16 / 2)}
                        width={isExpanded ? 20 : 16}
                        height={isExpanded ? 20 : 16}
                        style={{ pointerEvents: 'none' }}
                      >
                        <Typography sx={ICON_SX}>{s.icon}</Typography>
                      </foreignObject>
                    </PannableRect>
                  ) : (
                    /* Non-interactive mode: click to expand/collapse */
                    <g
                      style={{ cursor: 'pointer' }}
                      onClick={(e) => {
                        e.stopPropagation()
                        const newExpandedIds = expandedIds.includes(crop.id)
                          ? expandedIds.filter(i => i !== crop.id)
                          : [...expandedIds, crop.id]
                        dispatch(setExpandedIdsAction({ expandedIds: newExpandedIds }))
                      }}
                    >
                      <rect
                        data-phase-key={phaseKey}
                        x={x}
                        y={y}
                        width={phaseWidth}
                        height={barH}
                        fill={isParentOrgHovered ? darken(s.color, 0.2) : s.color}
                        rx={6}
                        stroke={PHASE_BORDER_COLOR}
                        strokeWidth={isParentOrgHovered ? 1 : 0.25}
                      />

                      {/* Phase icon */}
                      <foreignObject
                        x={x + 5}
                        y={isExpanded ? y + 6 : y + (barH / 2) - (16 / 2)}
                        width={isExpanded ? 20 : 16}
                        height={isExpanded ? 20 : 16}
                        style={{ pointerEvents: 'none' }}
                      >
                        <Typography sx={ICON_SX}>{s.icon}</Typography>
                      </foreignObject>

                    </g>
                  )}
                </g>
              )
            })}
          </g>
        )
      })}
    </>
  )
})
